import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFunnel } from '../../contexts/FunnelContext'
import { LoadingSpinner } from '../../components/funnel/LoadingSpinner'
import { CheckCircle, CreditCard } from 'lucide-react'
import { getSupabaseClient } from '../../lib/supabaseClient'
import { notifyLeadByEmail } from '../../lib/notifyLead'

const FoundingMemberCheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { state } = useFunnel()
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isAuthorized) return

    setIsSubmitting(true)
    try {
      const supabase = getSupabaseClient()
      await supabase.from('leads').insert({
        source: 'founding_member_checkout',
        full_name: state.fullName || null,
        email: state.email || null,
        phone: state.phone || null,
        payload: {
          selectedPackage: state.selectedPackage,
          address: state.address,
          preferredServiceWindow: state.preferredServiceWindow,
        },
      })
      notifyLeadByEmail({
        source: 'founding_member_checkout',
        full_name: state.fullName ?? undefined,
        email: state.email ?? undefined,
        phone: state.phone ?? undefined,
        payload: { selectedPackage: state.selectedPackage, address: state.address, preferredServiceWindow: state.preferredServiceWindow },
      })
    } catch (_) {
      // Continue even if lead save fails
    }
    // Subscription billing: team activates membership and sends payment link or invoice after this lead is received.
    await new Promise((resolve) => setTimeout(resolve, 1500))
    navigate('/welcome-member')
  }

  return (
    <div style={{ backgroundColor: 'rgb(246, 246, 241)', minHeight: '100vh', padding: '2rem 0' }}>
      <div className="mx-auto max-w-2xl px-8">
        {/* Reassurance Header */}
        <div className="bg-white p-8 rounded-xl mb-6 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#145F6B' }} />
          <h2 className="text-3xl font-semibold mb-3" style={{ color: 'rgb(15,15,15)' }}>
            Congratulations on Becoming a Founding Member!
          </h2>
          <p className="text-lg" style={{ color: 'rgb(76,76,75)' }}>
            You are making a smart choice for your home's future. Please confirm your details below
            to activate your membership.
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-white p-6 rounded-xl border-2 mb-6" style={{ borderColor: '#dee2e6' }}>
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'rgb(15,15,15)' }}>
            Your Founding Member Subscription
          </h3>
          <div className="space-y-3 mb-4">
            <div className="flex justify-between">
              <span style={{ color: 'rgb(76,76,75)' }}>Membership Level:</span>
              <span className="font-semibold" style={{ color: '#AB9381' }}>
                Founding Member
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'rgb(76,76,75)' }}>Recurring Rate:</span>
              <span className="font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                $199.00 / month
              </span>
            </div>
            <div className="border-t-2 pt-3 mt-3">
              <div className="flex justify-between">
                <span className="font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                  Amount Due Today:
                </span>
                <span className="text-2xl font-bold" style={{ color: '#145F6B' }}>
                  $0.00
                </span>
              </div>
            </div>
          </div>
          <div
            className="p-4 rounded-lg mt-4"
            style={{ backgroundColor: 'rgb(246, 246, 241)' }}
          >
            <p className="text-sm" style={{ color: 'rgb(76,76,75)' }}>
              <strong>Important:</strong> Your one-time Home Refresh purchase will be credited
              towards your first month's membership dues. Your first recurring payment of $199.00
              will be charged 30 days from today.
            </p>
          </div>
        </div>

        {/* Payment Information */}
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl border-2 mb-6">
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'rgb(15,15,15)' }}>
            Payment Method
          </h3>
          <div
            className="p-4 rounded-lg border-2 mb-4 flex items-center gap-4"
            style={{ borderColor: '#dee2e6', backgroundColor: 'rgb(246, 246, 241)' }}
          >
            <CreditCard className="w-8 h-8" style={{ color: '#145F6B' }} />
            <div>
              <div className="font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                **** **** **** 1234
              </div>
              <div className="text-sm" style={{ color: 'rgb(76,76,75)' }}>
                Expires 12/28
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isAuthorized}
                onChange={(e) => setIsAuthorized(e.target.checked)}
                className="mt-1 w-5 h-5"
                style={{ accentColor: '#145F6B' }}
                required
              />
              <span className="text-sm" style={{ color: 'rgb(15,15,15)' }}>
                I authorize Hudson My Home to charge this card $199.00 per month, starting 30 days
                from today, for my Founding Member membership. I understand I can cancel anytime.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={!isAuthorized || isSubmitting}
            className="w-full px-10 py-4 rounded-full font-bold text-lg transition-colors duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            style={{ backgroundColor: '#AB9381', color: 'white' }}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                <span>Activating Membership...</span>
              </div>
            ) : (
              'Activate My Membership Now'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default FoundingMemberCheckoutPage

